import React from "react";
import styled, { css } from "styled-components";
import { fontFamily, breakpoints, colors, sectionRhythm } from "../../../theme";
import TechnologyIcon from "../technology/TechnologyIcon";
import { blogProseBody, blogProseSectionTitle } from "../../styles/blog-prose";

const CARD_MIN_HEIGHT = "14rem";
const CARD_MIN_HEIGHT_DESKTOP = "16rem";
const CAPTIONED_IMAGE_HEIGHT = "22rem";

const proseBlockSpacing = css`
  margin-top: 1em;
`;

const CaseStudyCardBase = styled.div`
  box-sizing: border-box;
  border-radius: 40px;
  width: 100%;
`;

export const CaseStudySection = styled.section`
  margin: 0;
  padding-block: ${sectionRhythm.paddingY.mobile}px;

  & > :first-child {
    margin-top: 0;
  }

  @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
    padding-block: ${sectionRhythm.paddingY.desktop}px;
  }
`;

export const CaseStudySectionHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CaseStudySectionTitle = styled.h2`
  ${blogProseSectionTitle}
`;

export const CaseStudySectionBody = styled.div`
  ${blogProseBody}
`;

const CoverImage = styled.img`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ContainImage = styled.img`
  position: static;
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

export const CaseStudyCard = styled(CaseStudyCardBase)`
  position: relative;
  overflow: hidden;
  background: ${colors.white};
  min-height: ${CARD_MIN_HEIGHT};

  &:has(> ${ContainImage}) {
    min-height: auto;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    min-height: ${CARD_MIN_HEIGHT_DESKTOP};

    &:has(> ${ContainImage}) {
      min-height: auto;
    }
  }
`;

export const CaseStudyImage = ({
  src,
  alt,
  fit = "cover",
  bg = "transparent",
}) => {
  const Img = fit === "contain" ? ContainImage : CoverImage;
  return (
    <Img src={src} alt={alt} data-case-study-img style={{ background: bg }} />
  );
};

const CaseStudyStatCard = styled(CaseStudyCardBase)`
  background: ${colors.accent};
  color: ${colors.accentContent};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 0.8125rem;
  padding: 1.25rem 1.375rem 1.4375rem 1.375rem;
  font-weight: 500;
  line-height: 1.1;
  min-height: ${CARD_MIN_HEIGHT};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    gap: 1.375rem;
    padding: 1.25rem 1.375rem 2.4375rem 1.375rem;
    min-height: ${CARD_MIN_HEIGHT_DESKTOP};
  }
`;

const StatValue = styled.span`
  font-size: 40px;
  line-height: 1;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 52px;
  }
`;

const StatLabel = styled.span`
  font-size: 14px;
  text-align: right;
  line-height: 1.25;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 16px;
  }
`;

export const CaseStudyOverviewGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  container-type: inline-size;
  ${proseBlockSpacing}

  & > * {
    min-width: 0;
    width: 100%;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    align-items: stretch;
  }

  ${StatValue} {
    font-size: clamp(2rem, 18cqw, 3.25rem);
    line-height: 1;
    max-width: 100%;
  }

  ${StatLabel} {
    max-width: 100%;
  }
`;

const CaptionedImageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const CaptionedImageFrame = styled.div`
  width: 100%;
  height: ${CAPTIONED_IMAGE_HEIGHT};
  border: 1px solid #ccc;
  border-radius: 40px;
  overflow: hidden;
  background: ${colors.white};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    position: static;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CaptionText = styled.p`
  && {
    margin: 0;
    padding: 0;
    font-size: 0.95rem;
    font-weight: 400;
    line-height: 1.35;
    letter-spacing: -0.003em;
    color: ${colors.body};
    font-family: ${fontFamily.regular};
    text-align: center;
    background: none;
  }
`;

export const CaseStudyProcessStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  ${proseBlockSpacing}
`;

export const CaseStudyCaptionedImage = ({
  src,
  alt,
  caption,
  fit = "contain",
  bg = colors.white,
}) => (
  <CaptionedImageStack>
    <CaptionedImageFrame>
      <CaseStudyImage src={src} alt={alt} fit={fit} bg={bg} />
    </CaptionedImageFrame>
    {caption ? <CaptionText>{caption}</CaptionText> : null}
  </CaptionedImageStack>
);

export const CaseStudyOutcomeGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  ${proseBlockSpacing}

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex-direction: row;
    align-items: stretch;
    gap: 1.25rem;

    & > * {
      min-width: 0;
    }
  }
`;

export const CaseStudyOutcomeImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-width: 0;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex: 67 1 0;
  }

  ${CaseStudyCard} {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: auto;

    ${ContainImage} {
      position: static;
      display: block;
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      object-position: center;
      margin: 0;
    }
  }
`;

export const CaseStudyOutcomeStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.8125rem;
  width: 100%;
  min-width: 0;
  container-type: inline-size;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex: 33 1 0;
    gap: 1.375rem;
  }

  ${CaseStudyStatCard} {
    @media only screen and (min-width: ${breakpoints.desktop}) {
      padding: 1rem 1.25rem 1.5rem 1.25rem;
    }
  }

  ${StatValue} {
    font-size: clamp(2rem, 18cqw, 3.25rem);
    line-height: 1;
    max-width: 100%;
  }

  ${StatLabel} {
    max-width: 100%;
    line-height: 1.25;
  }
`;

const CaseStudyTechBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 2.5rem;
  border-radius: 40px;
  background: ${colors.accent};
  padding: 2.8125rem;
  ${proseBlockSpacing}
`;

export const CaseStudyStat = ({ value, label }) => (
  <CaseStudyStatCard>
    <StatValue>{value}</StatValue>
    <StatLabel>{label}</StatLabel>
  </CaseStudyStatCard>
);

export const CaseStudyTechIcons = ({ technologies }) => (
  <CaseStudyTechBar>
    {technologies.map((tech) => (
      <TechnologyIcon key={tech} name={tech} />
    ))}
  </CaseStudyTechBar>
);
